import React, { useSyncExternalStore } from 'react';
import {
    subscribeUploads,
    getUploadsSnapshot,
    UPLOAD_PREPARING,
    UPLOAD_UPLOADING,
    UPLOAD_UPLOADED,
    UPLOAD_ERROR,
    UPLOAD_CANCELLED,
} from './uploadManager';

// The uploads this tab is running, as a live array (see uploadManager).
export function useUploads() {
    return useSyncExternalStore(subscribeUploads, getUploadsSnapshot, getUploadsSnapshot);
}

export function formatBytes(bytes) {
    const value = Number(bytes) || 0;
    if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(value >= 10 * 1024 ** 3 ? 1 : 2)} GB`;
    if (value >= 1024 ** 2) return `${Math.round(value / 1024 ** 2)} MB`;
    if (value >= 1024) return `${Math.round(value / 1024)} KB`;
    return `${value} B`;
}

export function formatRate(bytesPerSecond) {
    const value = Number(bytesPerSecond) || 0;
    if (value <= 0) return '';
    if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MB/s`;
    return `${Math.round(value / 1024)} KB/s`;
}

export function formatEta(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    if (seconds < 60) return 'under a minute left';
    if (seconds < 3600) return `~${Math.round(seconds / 60)} min left`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return `~${hours} h ${minutes} min left`;
}

/*
 * What an asset row should say about its upload, from the live entry when
 * this tab is doing the sending and from the asset document otherwise.
 *
 * kinds: ready | live | finalizing | elsewhere | interrupted | error | cancelled
 */
export function describeUpload(asset, entry) {
    if (!asset) return { kind: 'ready' };
    if (asset.IsFinalized) return { kind: 'ready' };

    const total = Number(entry?.totalBytes ?? asset.UploadTotalBytes) || 0;

    if (entry) {
        const uploaded = Number(entry.uploadedBytes) || 0;
        const percent = total > 0 ? Math.min(100, Math.floor((uploaded / total) * 100)) : 0;
        if (entry.state === UPLOAD_PREPARING) {
            return { kind: 'live', percent: 0, label: 'Starting upload…', detail: '' };
        }
        if (entry.state === UPLOAD_UPLOADING) {
            const rate = Number(entry.bytesPerSecond) || 0;
            const eta = rate > 0 ? (total - uploaded) / rate : NaN;
            const parts = [`${formatBytes(uploaded)} / ${formatBytes(total)}`];
            const rateText = formatRate(rate);
            if (rateText) parts.push(rateText);
            const etaText = formatEta(eta);
            if (etaText) parts.push(etaText);
            return { kind: 'live', percent, label: `Uploading ${percent}%`, detail: parts.join(' · ') };
        }
        if (entry.state === UPLOAD_UPLOADED) {
            return { kind: 'finalizing', percent: 100, label: 'Finalizing…', detail: 'Upload complete — waiting for storage to confirm' };
        }
        if (entry.state === UPLOAD_ERROR) {
            return { kind: 'error', percent, label: 'Upload failed', detail: entry.error || '', canRetry: true };
        }
        if (entry.state === UPLOAD_CANCELLED) {
            return { kind: 'cancelled', percent, label: 'Upload cancelled', detail: `${formatBytes(uploaded)} of ${formatBytes(total)} kept — resume any time`, canResume: true };
        }
    }

    const uploaded = Number(asset.UploadedBytes) || 0;
    const percent = total > 0 ? Math.min(100, Math.floor((uploaded / total) * 100)) : 0;
    switch (asset.UploadState) {
        case 'uploaded':
            return { kind: 'finalizing', percent: 100, label: 'Finalizing…', detail: 'Upload complete — waiting for storage to confirm' };
        case 'uploading':
            return { kind: 'elsewhere', percent, label: `Uploading ${percent}%`, detail: `${formatBytes(uploaded)} / ${formatBytes(total)} · from another tab or device` };
        case 'interrupted':
        default:
            if (total > 0 && uploaded > 0) {
                return { kind: 'interrupted', percent, label: `Upload interrupted at ${percent}%`, detail: `${formatBytes(uploaded)} of ${formatBytes(total)} kept — pick the same file to continue`, canResume: true };
            }
            return { kind: 'interrupted', percent: 0, label: 'Upload did not complete', detail: 'Pick the file again to upload it', canResume: true };
    }
}

/*
 * Progress bar + one-line status for an asset that is not ready yet.
 * `compact` is the media-bin chip: bar and label only.
 */
export default function AssetUploadStatus({ asset, entry, onResume, onRetry, onCancel, compact = false }) {
    const view = describeUpload(asset, entry);
    if (view.kind === 'ready') return null;

    const showBar = view.kind !== 'interrupted' || view.percent > 0;
    return (
        <div className={`asset-upload-status ${view.kind}${compact ? ' compact' : ''}`} role="status" aria-live="polite">
            {showBar && (
                <div className="asset-upload-bar" aria-hidden="true">
                    <div className="asset-upload-bar-fill" style={{ width: `${view.percent}%` }} />
                </div>
            )}
            <div className="asset-upload-line">
                <span className="asset-upload-label">{view.label}</span>
                {!compact && view.detail && <span className="asset-upload-detail">{view.detail}</span>}
            </div>
            {!compact && (
                <div className="asset-upload-actions">
                    {view.kind === 'live' && onCancel && (
                        <button type="button" className="btn btn-link asset-upload-action" onClick={() => onCancel(asset, entry)}>
                            Cancel
                        </button>
                    )}
                    {view.canRetry && onRetry && (
                        <button type="button" className="btn btn-link asset-upload-action" onClick={() => onRetry(asset, entry)}>
                            Retry
                        </button>
                    )}
                    {view.canResume && onResume && (
                        <button type="button" className="btn btn-link asset-upload-action" onClick={() => onResume(asset, entry)}>
                            {view.kind === 'cancelled' || (view.percent > 0) ? 'Resume' : 'Upload again'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
